/** English text for the UI. Every user-facing string lives here. */
export const en = {
  app: { title: "Predictions analyst", tagline: "Describe an analysis; an agent builds a program you can run again and again." },
  theme: { toggle: "Switch between light and dark" },
  account: {
    signIn: "Sign in with Google",
    signOut: "Sign out",
    demoTitle: "Try the demo",
    demoBody: "Use the demo account to explore the analyst. No Google account is needed.",
    demoUsername: "Username",
    demoPassword: "Password",
    demoSignIn: "Sign in to demo",
    demoSigningIn: "Signing in…",
    demoFailed: "Demo sign-in failed. Check your username and password, then try again.",
    demoUnavailable: "Demo sign-in is unavailable right now. Try again shortly.",
    demoExpired: "The sign-in page has expired. Reload it and try again.",
    demoRateLimited: "Too many sign-in attempts. Wait a minute and try again.",
    demoNotice: "Demo workspace — programs and reports here are shared with other demo visitors.",
    signInTitle: "Sign in to continue",
    signInBody: "Sign in to create programs, run analyses, and read reports.",
    developmentNote: "Development sign-in: no Google account is used.",
    unavailable: "Sign-in is not configured on this server.",
    denied: "This account cannot use the site",
    loginFailed: "Sign-in did not complete. Try again.",
  },
  programs: {
    heading: "Programs",
    new: "New program",
    empty: "No programs yet. Describe an analysis to create the first one.",
    loadFailed: "Programs could not be loaded.",
    status: { authoring: "Being written", ready: "Ready", failed: "Failed" },
    pick: "Choose a program, or create a new one.",
    version: "Version {{version}}",
  },
  create: {
    heading: "New program",
    label: "What should the program do?",
    help: "Describe the analysis: what to compute, for which inputs (a date, a game, a team), and what the report should show.",
    submit: "Build the program",
    submitting: "Starting…",
    examplesHeading: "Examples",
    examples: [
      "For a given NBA date, list every game with its Crown spread and total from open to now, and flag games whose spread moved by at least a chosen number of points.",
      "For an NBA game, compare each team's last ten results with the Crown moneyline, and show the win probability the moneyline implies for each side.",
      "For an NBA game, show Kalshi and Polymarket prices for each team over the hour before tip-off beside the Crown moneyline.",
    ],
    tooShort: "Describe the analysis in at least 10 characters.",
    tooLong: "Keep the request under 4,000 characters.",
  },
  program: {
    loadFailed: "The program could not be loaded.",
    request: "Request",
    authoring: "The agent is writing this program. This can take several minutes.",
    authoringQueued: "Waiting to start writing the program.",
    noVersion: "This program has no runnable version.",
    attempt: "Attempt {{attempt}}",
    attemptPassed: "passed the check",
    attemptFailed: "failed the check",
    sessionFailed: "Writing the program failed",
    revise: "Change the request",
    reviseHelp: "Edit the request; the agent writes a new version and keeps this one.",
    reviseSubmit: "Write a new version",
    revising: "A new version is being written.",
    files: "Program files",
    agentSetup: "Agent setup",
    agentHarness: "Codex · ChatGPT subscription · {{model}}",
    operatorModel: "operator-configured model",
    runPrompt: "Run prompt",
    skills: "Skills ({{count}})",
    noSkills: "No skills.",
    tools: "Allowed tools",
  },
  run: {
    heading: "Run",
    submit: "Run",
    submitting: "Starting…",
    history: "Runs",
    noRuns: "No runs yet. Fill in the parameters and run the program.",
    status: { queued: "Queued", running: "Running", succeeded: "Done", insufficient: "No answer", failed: "Failed" },
    required: "Required",
    pickCompetition: "Competition",
    pickDate: "Date",
    pickGame: "Game",
    noGames: "No games on this date.",
    gamesFailed: "Games could not be loaded.",
    chooseGame: "Choose a game",
    choose: "Choose…",
    receipt: "{{calls}} data calls, {{fromStore}} from stored data, {{credits}} credits",
  },
  report: {
    empty: "Choose a run to read its report.",
    waiting: "The program is running…",
    insufficient: "The data cannot answer this",
    missing: "Missing: {{missing}}",
    failed: "The run failed",
    raw: "Data as JSON",
    noRows: "No rows.",
    log: "Run log",
    agentPage: "Agent-generated report",
    agentTrace: "Agent trace",
    traceQueued: "Waiting to start…",
    traceWorking: "Working…",
    traceUsing: "Running {{tool}}…",
    traceUnavailable: "No trace events were recorded for this run.",
    traceInput: "Input",
    traceResult: "Result preview",
    traceResultTruncated: "Preview shortened. The agent received the tool's full response.",
    traceFinishedAt: "Finished at {{time}}",
    traceOmitted: "{{count}} earlier trace events omitted.",
    traceTool: { running: "Running", completed: "Completed", cancelled: "Cancelled", failed: "Failed ({{code}})" },
    traceEvent: {
      starting: "Starting agent…", thinking: "Thinking…", reporting: "Preparing response…",
      completed: "Report completed", insufficient: "Run completed with insufficient data", failed: "Run failed", timeout: "Run timed out",
      tool: "Tool call", message: "Progress update",
    },
    agentReceipt: "{{harness}} · {{model}} · {{input}} input / {{output}} output tokens · subscription billing",
    stepCredits: "{{credits}} credits · {{stored}} stored calls",
  },
  states: { loading: "Loading…", retry: "Try again" },
};

export type Messages = typeof en;
